import { ReactNode } from "react";
import styles from "../TableTemplate/TableTemplates.module.css";
import SearchableDropdown from "../../../components/SearchableDropdown/SearchableDropdown";
import { postUser } from "../../../helper/http";
import TableFileUpload from "../../../components/TableFileUpload/TableFileUpload";

type TFunc = (key: string) => string;

interface FieldRendererProps {
    col: any;
    cellValue: string | boolean | number | null | any[];
    isEditing: boolean;
    inline: {
        handleEditChange: (field: string, value: any) => void;
        handleKey: (e: React.KeyboardEvent) => void;
        editBuffer?: Record<string, any>;
        firstFieldRef?: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
    };
    t: TFunc;
    props: {
        maps?: Record<string, Record<string, string>>;
    };
    options: Record<any, any[]>;
    setOptions: React.Dispatch<React.SetStateAction<Record<any, any[]>>>;
    isFirstField?: boolean;
    errors?: Record<string, string>;
}

export const isBooleanField = (col: any): boolean => {
    return col.file_type === "boolean" || col.type === "boolean" || 
           col.file_type === "bool" || col.type === "bool";
};

export const isDateField = (col: any): boolean => {
    return col.file_type === "date" || col.type === "date";
};

export const isDateTimeField = (col: any): boolean => {
    return col.file_type === "timestamp" || col.type === "timestamp" ||
           col.file_type === "datetime" || col.type === "datetime";
};

export const isTextAreaField = (col: any): boolean => {
    return col.file_type === "text" || col.type === "text" ||
           col.file_type === "textarea" || col.type === "textarea";
};

export const isSelectField = (col: any): boolean => {
    return col.file_type === "fk" || col.type === "fk" ||
           col.file_type === "select" || col.type === "select";
};

export const isFileField = (col: any): boolean => {
    return col.file_type === "file" || col.type === "file";
};

export const isNumberField = (col: any): boolean => {
    return col.file_type === "int" || col.type === "int" || 
           col.file_type === "integer" || col.type === "integer";
};

export const isDoubleField = (col: any): boolean => {
    return col.file_type === "double" || col.type === "double" || 
           col.file_type === "float" || col.type === "float" ||
           col.file_type === "decimal" || col.type === "decimal";
};

export const isTimeField = (col: any): boolean => {
    return col.file_type === "time" || col.type === "time";
};

export const isColorField = (col: any): boolean => {
    return col.file_type === "color" || col.type === "color";
};

export const isJsonField = (col: any): boolean => {
    return col.file_type === "json" || col.type === "json";
};

export const isMultiSelectField = (col: any): boolean => {
    return col.file_type === "multiselect" || col.type === "multiselect";
};

export const isPasswordField = (col: any): boolean => {
    return col.file_type === "password" || col.type === "password";
};

const autoFields = ["id", "created_at", "updated_at"];

export const isAutoField = (fieldName: string): boolean => {
    return autoFields.includes(fieldName.toLowerCase());
};


export const getField = ({
    col,
    cellValue,
    isEditing,
    inline,
    t,
    props,
    options,
    setOptions,
    isFirstField = false,
    errors = {},
}: FieldRendererProps): ReactNode => {
    
    const isBoolean = isBooleanField(col);
    const isAuto = isAutoField(col.name);
    const isDate = isDateField(col);
    const isDateTime = isDateTimeField(col);
    const isText = isTextAreaField(col);
    const isSelect = isSelectField(col);
    const isFile = isFileField(col);
    const isNumber = isNumberField(col);
    const isDouble = isDoubleField(col);
    const isTime = isTimeField(col);
    const isColor = isColorField(col);
    const isJson = isJsonField(col);
    const isMultiSelect = isMultiSelectField(col);
    const isPassword = isPasswordField(col);

    if (isEditing) {

        const editBuffer = inline.editBuffer ?? {};
        let value = col.name in editBuffer ? editBuffer[col.name] : cellValue;
        
        if ((value === null || value === undefined || value === '') && !isAuto) {
            if (isColor) value = '#000000';
            else if (isJson) value = '{}';
            else if (isDate) value = new Date().toISOString().split('T')[0];
            else if (isDateTime) value = new Date().toISOString().slice(0, 16);
        }
        
        let displayValue = value;
        if (isDate && value) displayValue = value.toString().replace(/T.*/, '');
        if (isDateTime && value) displayValue = value.toString().replace('T', ' ').replace(/\.\d+Z/, '');
        if (isTime && value) displayValue = value.toString().replace(/.*T/, '').substring(0, 5);

        if (isAuto) {
            return (
                <input
                    type="text"
                    className={styles.inlineInput}
                    value={displayValue ?? (t("autoGenerated") || "Auto")}
                    disabled={true}
                    onKeyDown={inline.handleKey}
                />
            );
        }

        if (isBoolean) {
            return (
                <input
                    type="checkbox"
                    className={styles.inlineCheckbox}
                    checked={value === true}
                    onChange={e => inline.handleEditChange(col.name, e.target.checked)}
                    onKeyDown={inline.handleKey}
                    ref={isFirstField && inline.firstFieldRef ? inline.firstFieldRef as React.RefObject<HTMLInputElement> : undefined}
                />
            );
        }

        if (isNumber) {
            return (
                <input
                    type="number"
                    step="1"
                    className={styles.inlineInput}
                    value={displayValue ?? ""}
                    onChange={e => inline.handleEditChange(
                        col.name,
                        e.target.value === "" ? null : parseInt(e.target.value, 10)
                    )}
                    onKeyDown={inline.handleKey}
                    ref={isFirstField && inline.firstFieldRef ? inline.firstFieldRef as React.RefObject<HTMLInputElement> : undefined}
                />
            );
        }

        if (isDouble) {
            return (
                <input
                    type="number"
                    step="0.01"
                    className={styles.inlineInput}
                    value={displayValue ?? ""}
                    onChange={e => inline.handleEditChange(
                        col.name,
                        e.target.value === "" ? null : parseFloat(e.target.value)
                    )}
                    onKeyDown={inline.handleKey}
                    ref={isFirstField ? inline.firstFieldRef as any : undefined} 
                />
            );
        }

        if (isColor) {
            let colorValue = value ?? "#000000";
            if (colorValue && typeof colorValue === 'string' && !colorValue.startsWith('#')) {
                colorValue = '#' + colorValue;
            }
            
            return (
                <div className={styles.colorInputGroup}>
                    <input
                        type="color"
                        className={styles.inlineColorPicker}
                        value={colorValue}
                        onChange={e => inline.handleEditChange(col.name, e.target.value)}
                        onKeyDown={inline.handleKey}
                        ref={isFirstField && inline.firstFieldRef ? inline.firstFieldRef as React.RefObject<HTMLInputElement> : undefined}
                    />
                    <input
                        type="text"
                        className={`${styles.inlineInput} ${styles.colorTextInput}`}
                        maxLength={7}
                        value={colorValue}
                        onChange={e => {
                            let newColorValue = e.target.value;
                            if (newColorValue && !newColorValue.startsWith('#')) {
                                newColorValue = '#' + newColorValue;
                            }
                            inline.handleEditChange(col.name, newColorValue);
                        }}
                        placeholder="#000000"
                        onKeyDown={inline.handleKey}
                    />
                </div>
            );
        }

        if (isJson) {
            let jsonString = '';
            
            if (typeof value === 'object' && value !== null) {
                jsonString = JSON.stringify(value, null, 2);
            } else if (typeof value === 'string') {
                jsonString = value;
            } else if (value !== null && value !== undefined) {
                jsonString = JSON.stringify(value, null, 2);
            }
            
            return (
                <div className={styles.jsonInputWrapper}>
                    <textarea
                        className={styles.inlineInput}
                        value={jsonString}
                        onChange={e => inline.handleEditChange(col.name, e.target.value)}
                        rows={4}
                        placeholder='{"key": "value"}'
                        title={t('jsonFormatHint')}
                        onKeyDown={(e) => {
                            if (e.key === "Escape") {
                                inline.handleKey(e);
                            }
                        }}
                        ref={isFirstField && inline.firstFieldRef ? inline.firstFieldRef as React.RefObject<HTMLTextAreaElement> : undefined}
                    />
                    <span className={styles.jsonHint}>
                        {t('jsonExample')}
                    </span>
                </div>
            );
        }

        if (isPassword) {
            return (
                <input
                    type="password"
                    className={styles.inlineInput}
                    value={value ?? ""}
                    onChange={e => inline.handleEditChange(col.name, e.target.value)}
                    placeholder="••••••••"
                    onKeyDown={inline.handleKey}
                    ref={isFirstField && inline.firstFieldRef ? inline.firstFieldRef as React.RefObject<HTMLInputElement> : undefined}
                />
            );
        }

        if (isMultiSelect) {
            let parsedValue = value;
            if (typeof value === 'string') {
                try { parsedValue = JSON.parse(value); }
                catch { parsedValue = []; }
            }
            
            const hardcodedOptions = [
                { label: "Option 1", value: "option1" },
                { label: "Option 2", value: "option2" },
                { label: "Option 3", value: "option3" },
                { label: "Option 4", value: "option4" },
                { label: "Option 5", value: "option5" },
            ];

            return (
                <SearchableDropdown
                multiple
                label={""}
                options={hardcodedOptions}
                value={Array.isArray(parsedValue) ? parsedValue : []}
                onChange={values => inline.handleEditChange(col.name, values)}
                onSearch={() => {}}
                onKeyDown={inline.handleKey}
                />
            );
        }

        if (isSelect) {
            const defaultOptions = props.maps?.[col.relation_module]
                ? Object.keys(props.maps[col.relation_module]).map((key: string) => ({
                    label: props.maps![col.relation_module][key], 
                    value: key
                })) 
                : [{label: cellValue?.toString(), value: cellValue?.toString()}];

            const handleSearch = async (text: string) => {
                const payload = {
                module_id: +col.relation_module,
                filter: text
                };

                const res = await postUser("crud/select", payload);

                setOptions({
                ...options, 
                [col.relation_module]: Object.keys(res.list).map(key => ({
                    label: res.list[key],
                    value: key,
                })),
                });
            };

            const handleOpen = async () => {
                if (!options[col.relation_module] || options[col.relation_module].length === 0) {
                await handleSearch("");
                }
            };

            return (
                <SearchableDropdown
                label={""}
                options={[
                    ...options[col.relation_module] || defaultOptions, 
                    {label: "No value", value: null}
                ]}
                value={(value ?? "").toString()}
                onChange={newValue => inline.handleEditChange(col.name, newValue)}
                onSearch={handleSearch}
                onOpen={handleOpen}
                onKeyDown={inline.handleKey}
                autoOpen={true} 
                />
            );
        }

        if (isFile) {
            return (
                <TableFileUpload
                    value={value?.toString() || null}
                    onChange={(file) => {
                        if (file) {
                            inline.handleEditChange(col.name, file.name);
                        } else {
                            inline.handleEditChange(col.name, null);
                        }
                    }}
                />
            );
        }

        if (isText) {
            return (
                <textarea
                    className={styles.inlineInput}
                    style={{ whiteSpace: 'pre-wrap' }}
                    value={value ?? ""}
                    onChange={e => {
                        inline.handleEditChange(col.name, e.target.value);
                    }}
                    ref={isFirstField && inline.firstFieldRef ? inline.firstFieldRef as React.RefObject<HTMLTextAreaElement> : undefined}
                />
            );
        }

        if (isDate) {
        return (
            <input
            type="date"
            className={styles.inlineInput}
            value={displayValue ?? ""}
            onChange={e => inline.handleEditChange(col.name, e.target.value)}
            onKeyDown={(e) => {
                if (e.key === "Enter" && !e.ctrlKey && !e.shiftKey && !e.altKey) {
                e.preventDefault();
                (e.target as HTMLInputElement).showPicker?.();
                if (!('showPicker' in e.target)) {
                    inline.handleKey(e);
                }
                } else {
                inline.handleKey(e);
                }
            }}
            ref={isFirstField && inline.firstFieldRef ? inline.firstFieldRef as React.RefObject<HTMLInputElement> : undefined}
            />
        );
        }

        if (isDateTime) {
            return (
                <input
                type="datetime-local"
                className={styles.inlineInput}
                value={displayValue ?? ""}
                onChange={e => inline.handleEditChange(col.name, e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.ctrlKey && !e.shiftKey && !e.altKey) {
                    e.preventDefault();
                    (e.target as HTMLInputElement).showPicker?.();
                    if (!('showPicker' in e.target)) {
                        inline.handleKey(e);
                    }
                    } else {
                    inline.handleKey(e);
                    }
                }}
                ref={isFirstField && inline.firstFieldRef ? inline.firstFieldRef as React.RefObject<HTMLInputElement> : undefined}
                />
            );
        }

        if (isTime) {
            return (
                <input
                type="time"
                className={styles.inlineInput}
                value={displayValue ?? ""}
                onChange={e => inline.handleEditChange(col.name, e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.ctrlKey && !e.shiftKey && !e.altKey) {
                    e.preventDefault();
                    (e.target as HTMLInputElement).showPicker?.();
                    if (!('showPicker' in e.target)) {
                        inline.handleKey(e);
                    }
                    } else {
                    inline.handleKey(e);
                    }
                }}
                ref={isFirstField && inline.firstFieldRef ? inline.firstFieldRef as React.RefObject<HTMLInputElement> : undefined}
                />
            );
        }

        return (
            <input
                type="text"
                className={styles.inlineInput}
                value={value ?? ""}
                onChange={e => inline.handleEditChange(col.name, e.target.value)}
                onKeyDown={inline.handleKey}
                ref={isFirstField && inline.firstFieldRef ? inline.firstFieldRef as React.RefObject<HTMLInputElement> : undefined}
            />
        );
    }

    if (isBoolean) {
        return (
            <input
                type="checkbox"
                className={styles.inlineCheckboxDefault}
                checked={cellValue === true}
                readOnly
            />
        );
    }

    if (isDate && cellValue) return new Date(cellValue.toString()).toLocaleDateString();
    if (isDate && !cellValue) return <span className={styles.emptyValue}>Not specified</span>;

    if (isDateTime && cellValue) {
        const date = new Date(cellValue.toString());
        return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    }
    if (isDateTime && !cellValue) return <span className={styles.emptyValue}>Not specified</span>;

    if (isTime && cellValue) return cellValue.toString().substring(0, 5);

    if (isColor && cellValue) {
        let displayColor = cellValue.toString();
        if (!displayColor.startsWith('#')) displayColor = '#' + displayColor;
        return (
            <div className={styles.colorDisplay}>
                <div className={styles.colorSwatch} style={{ backgroundColor: displayColor }}></div>
                <span>{displayColor}</span>
            </div>
        );
    }
    if (isColor && !cellValue) {
        return (
            <div className={styles.colorDisplay}>
                <div className={styles.colorSwatch} style={{ backgroundColor: "#cccccc" }}></div>
                <span className={styles.emptyValue}>Not specified</span>
            </div>
        );
    }

    if (isJson && cellValue) {
        let jsonStr = '';
        
        if (typeof cellValue === "object" && cellValue !== null) {

            jsonStr = JSON.stringify(cellValue, null, 2);
        } else {
            jsonStr = cellValue.toString();
        }
        
        return jsonStr.length > 50 ? jsonStr.slice(0, 50) + "..." : jsonStr;
    }
    if (isJson && !cellValue) return <span className={styles.emptyValue}>Empty {}</span>;

    if (isPassword && cellValue) return "••••••••";

    if (isMultiSelect && cellValue) {
        let values = cellValue;
        if (typeof cellValue === "string") {
            try { values = JSON.parse(cellValue); }
            catch { return cellValue.toString(); }
        }
        
        if (Array.isArray(values)) {
            const labels = values.map(val => props.maps?.[col.relation_module]?.[val] || val);
            return labels.join(", ");
        }
        return cellValue.toString();
    }

    if (isSelect && cellValue) {
        return props.maps?.[col.relation_module]?.[cellValue.toString()] || cellValue;
    }

    if (cellValue && cellValue.toString().length > 50) {
        return cellValue.toString().slice(0, 50) + "...";
    }

    return String(cellValue ?? "");
};